import { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Modal dialog component with focus trap and escape handling.
 * Minimal implementation without headlessui dependency.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is visible
 * @param {function} props.onClose - Called when modal should close
 * @param {string} [props.title] - Modal title
 * @param {'sm'|'md'|'lg'|'xl'|'2xl'|'3xl'|'4xl'|'full'} [props.size='md'] - Modal width.
 *   'full' fills the viewport and turns the body into a flex column so children
 *   can own their own scrolling (e.g. a large grid).
 * @param {boolean} [props.showCloseButton=true] - Show X button
 * @param {string} [props.className] - Additional classes for content
 */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  showCloseButton = true,
  className,
  children,
}) {
  const dialogRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store previously focused element and focus dialog on open
  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement;
      // Delay focus to allow animation
      setTimeout(() => {
        dialogRef.current?.focus();
      }, 10);
    } else if (previousActiveElement.current) {
      previousActiveElement.current.focus();
    }
  }, [open]);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    full: 'max-w-none h-full flex flex-col',
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby={title ? 'modal-title' : undefined}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Dialog positioning */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        {/* Dialog panel */}
        <div
          ref={dialogRef}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className={cn(
            'relative w-full bg-white rounded-lg shadow-lg',
            'transform transition-all',
            'focus:outline-none',
            sizes[size],
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              {title && (
                <h3
                  id="modal-title"
                  className="text-lg font-semibold text-neutral-900"
                >
                  {title}
                </h3>
              )}
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className={cn(
                    'p-1 rounded-md text-neutral-500',
                    'hover:text-neutral-700 hover:bg-neutral-100',
                    'focus:outline-none focus:ring-2 focus:ring-primary-500',
                    'transition-colors duration-150',
                    !title && 'ml-auto'
                  )}
                >
                  <span className="sr-only">Close</span>
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className={cn('p-4', size === 'full' && 'flex-1 min-h-0 flex flex-col')}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal footer for actions.
 */
export function ModalFooter({ className, children, ...props }) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-3 pt-4 border-t border-neutral-200 -mx-4 -mb-4 px-4 pb-4 mt-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
